/**
 * ProLayout 专用：`layout` token、`contentStyle` 背景、与 `initialState.settings` 合并逻辑。
 * 组件级 antd token（Button、Menu 等）见 `antdThemeByMode.ts`；路由与 layout 开关见 `defaultSettings.ts`。
 */
import type {
  ProLayoutProps,
  Settings as ProLayoutSettings,
} from '@ant-design/pro-components';
import type { DeepPartial, ProAliasToken } from '@ant-design/pro-provider';
import type { CSSProperties } from 'react';

/**
 * Pro 布局 token 与 `antdThemeByMode` 对齐（两档键一致，只换值）：
 * - 浅色：`layout.bgLayout`、`pageContainer.colorBgPageContainer` 均为 `#ffffff`（与 antd `colorBgLayout` / `colorBgContainer` / `colorPrimary` 根三色一致）。
 * - 深色：上述与 antd 一致，均为 `#171717`。
 */

/** ProSettings 类型未声明 token，但 ProLayout 运行时支持，用于 initialState.settings */
export type LayoutSettingsWithToken = Partial<ProLayoutSettings> & {
  token?: DeepPartial<ProAliasToken>;
} & Pick<ProLayoutProps, 'contentStyle' | 'menuItemRender' | 'subMenuItemRender'>;

/**
 * ProLayout 侧栏、内容区、整体布局背景等（与 navTheme 联动）。
 * 文档：https://procomponents.ant.design/components/layout#通过-token-修改样式
 * 类型见：node_modules/@ant-design/pro-provider/es/typing/layoutToken.d.ts
 */
export function getProLayoutSettingsToken(
  nav: 'light' | 'realDark',
): DeepPartial<ProAliasToken> {
  if (nav === 'realDark') {
    return {
      layout: {
        /** 整站布局底层背景（.ant-pro-layout-bg-list，与 antd colorBgLayout 对齐） */
        bgLayout: '#171717',
        sider: {
          /** 左侧菜单区域背景 */
          colorMenuBackground: '#121212',
        },
        pageContainer: {
          /** 主内容区背景（与 contentStyle 同源） */
          colorBgPageContainer: '#171717',
        },
      },
    };
  }
  return {
    layout: {
      bgLayout: '#ffffff',
      sider: {
        colorMenuBackground: '#fafafa',
      },
      pageContainer: {
        colorBgPageContainer: '#ffffff',
      },
    },
  };
}

/**
 * 主内容区背景：与 `layout.pageContainer.colorBgPageContainer` 同源。
 * Pro 的 layout token 经 css-in-js 合并后，`.ant-pro-layout-content` 上的背景不一定稳定命中，
 * `ProLayout` 的 `contentStyle` 会作用在 `Layout.Content` 上（行内样式），与文档示例一致。
 */
export function getProLayoutContentBackgroundStyle(
  nav: 'light' | 'realDark',
): CSSProperties {
  const bg = getProLayoutSettingsToken(nav).layout?.pageContainer
    ?.colorBgPageContainer;
  return bg ? { backgroundColor: bg } : {};
}

/** 合并 defaultSettings / SettingDrawer 里已有 token，再按深浅色覆盖布局相关字段 */
export function mergeProLayoutToken(
  base: DeepPartial<ProAliasToken> | undefined,
  nav: 'light' | 'realDark',
): DeepPartial<ProAliasToken> {
  const patch = getProLayoutSettingsToken(nav);
  const l = patch.layout;
  const layoutMerged = {
    ...base?.layout,
    ...l,
    sider: {
      ...base?.layout?.sider,
      ...l?.sider,
    },
    pageContainer: {
      ...base?.layout?.pageContainer,
      ...l?.pageContainer,
    },
    header: {
      ...base?.layout?.header,
      ...l?.header,
    },
  };

  /*
   * @ant-design/pro-provider 的 getLayoutDesignToken 第一个参数是整段 props.token，
   * 但内部只从「根上」读 bgLayout / sider / pageContainer（见 layoutToken.js），
   * 不会读 token.layout.*，导致仅配置 token.layout 时侧栏/整站背景不生效。
   * 合并后把 layout 里的字段同步摊到 token 根上，与 Pro 内部合并逻辑对齐。
   */
  return {
    ...base,
    ...patch,
    layout: layoutMerged,
    ...(l
      ? {
          bgLayout: l.bgLayout,
          sider: layoutMerged.sider,
          pageContainer: layoutMerged.pageContainer,
          header: layoutMerged.header,
        }
      : {}),
  } as DeepPartial<ProAliasToken>;
}
